if (!customElements.get('bundle-save-popup')) {
  customElements.define(
    'bundle-save-popup',
    class BundleSavePopup extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector('[data-bundle-save-overlay]');
        this.dialog = this.querySelector('.bundle-save-popup__dialog');
        this.descriptionEl = this.querySelector('[data-bundle-save-description]');
        this.list = this.querySelector('[data-bundle-save-list]');
        this.itemTemplate = this.querySelector('[data-bundle-save-item-template]');
        this.addAllButton = this.querySelector('[data-bundle-save-add-all]');
        this.addSelectedButton = this.querySelector('[data-bundle-save-add-selected]');

        this.cache = {};

        this.overlay.addEventListener('click', () => this.hide());
        this.querySelectorAll('[data-bundle-save-close]').forEach((button) =>
          button.addEventListener('click', () => this.hide())
        );
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') this.hide();
        });
        this.addAllButton.addEventListener('click', () => this.addToCart(true));
        this.addSelectedButton.addEventListener('click', () => this.addToCart(false));

        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          this.handleCartUpdate(event);
        });
      }

      async handleCartUpdate(event) {
        const handle = event.cartData && event.cartData.handle;
        if (!handle) return;

        try {
          const payload = await this.getBundleData(handle);
          if (!payload || !payload.accessories || payload.accessories.length === 0) return;
          this.render(payload);
          this.show(document.activeElement);
        } catch (error) {
          console.error('Bundle & Save: could not load bundle data', error);
        }
      }

      async getBundleData(handle) {
        if (Object.prototype.hasOwnProperty.call(this.cache, handle)) return this.cache[handle];

        const response = await fetch(`/products/${handle}?section_id=bundle-save-data`);
        if (!response.ok) {
          this.cache[handle] = null;
          return null;
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('[data-bundle-save-payload]');
        const payload = script ? JSON.parse(script.textContent) : null;

        this.cache[handle] = payload;
        return payload;
      }

      render(payload) {
        this.currentPayload = payload;
        this.descriptionEl.textContent = payload.description;
        this.list.innerHTML = '';

        payload.accessories.forEach((accessory) => {
          const item = this.itemTemplate.content.firstElementChild.cloneNode(true);
          const checkbox = item.querySelector('[data-bundle-save-item-checkbox]');
          const image = item.querySelector('[data-bundle-save-item-image]');
          const title = item.querySelector('[data-bundle-save-item-title]');
          const price = item.querySelector('[data-bundle-save-item-price]');
          const soldOut = item.querySelector('[data-bundle-save-item-soldout]');

          const variantSelect = item.querySelector('[data-bundle-save-item-variant-select]');
          const colorVariants = accessory.colorVariants || [];

          if (colorVariants.length > 1) {
            colorVariants.forEach((v) => {
              const option = document.createElement('option');
              option.value = v.id;
              option.textContent = v.color;
              option.dataset.price = v.price;
              option.dataset.available = v.available;
              option.dataset.image = v.image;
              variantSelect.appendChild(option);
            });

            variantSelect.classList.remove('hidden');

            const applyVariant = (v) => {
              checkbox.value = v.id;
              checkbox.checked = v.available;
              checkbox.disabled = !v.available;
              image.src = v.image;
              price.textContent = window.Shopify.formatMoney(v.price);
              if (!v.available) {
                soldOut.classList.remove('hidden');
                item.classList.add('bundle-save-popup__item--soldout');
              } else {
                soldOut.classList.add('hidden');
                item.classList.remove('bundle-save-popup__item--soldout');
              }
            };

            applyVariant(colorVariants[0]);

            variantSelect.addEventListener('change', () => {
              const selected = colorVariants.find((v) => String(v.id) === variantSelect.value);
              if (selected) applyVariant(selected);
            });
          } else {
            checkbox.value = accessory.variantId;
            checkbox.checked = accessory.available;
            checkbox.disabled = !accessory.available;
            image.src = accessory.image;
            price.textContent = window.Shopify.formatMoney(accessory.price);

            if (!accessory.available) {
              soldOut.classList.remove('hidden');
              item.classList.add('bundle-save-popup__item--soldout');
            }
          }

          image.alt = accessory.title;
          title.textContent = accessory.title;

          this.list.appendChild(item);
        });
      }

      show(opener) {
        this.openedBy = opener && opener !== document.body ? opener : null;
        setTimeout(() => {
          this.classList.add('active');
          trapFocus(this, this.dialog);
        });
        document.body.classList.add('overflow-hidden');
      }

      hide() {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.openedBy);
      }

      getCart() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      async addToCart(all) {
        const checkboxes = Array.from(this.list.querySelectorAll('[data-bundle-save-item-checkbox]'));
        const selected = checkboxes.filter((checkbox) => !checkbox.disabled && (all || checkbox.checked));
        if (selected.length === 0) return;

        const button = all ? this.addAllButton : this.addSelectedButton;
        const spinner = button.querySelector('.loading__spinner');
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        const bundleGroup = `bundle-${this.currentPayload.productId}-${Date.now()}`;
        const items = selected.map((checkbox) => ({
          id: Number(checkbox.value),
          quantity: 1,
          properties: { _bundle_group: bundleGroup },
        }));

        const cart = this.getCart();
        const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : [];

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items, sections }),
          });
          const data = await response.json();

          if (!response.ok) throw new Error(data.description || 'Bundle & Save: add to cart failed');

          if (cart) {
            cart.renderContents(data);
            cart.classList.remove('is-empty');
            if (cart.tagName.toLowerCase() === 'cart-drawer') cart.open();
          } else {
            window.location.href = `${window.Shopify.routes.root}cart`;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-save-popup', cartData: data });
          this.hide();
        } catch (error) {
          console.error('Bundle & Save: add to cart failed', error);
        } finally {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }
    }
  );
}
